
import React, { useState } from 'react';
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Type, Trash2 } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Block } from "../types";
import { blockConfig } from '../config/blockConfig';
import { TextBlock } from './TextBlock';
import { ImageBlock } from './ImageBlock';
import { ButtonBlock } from './ButtonBlock';
import { EmbedBlock } from './EmbedBlock';

interface SortableBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  onDelete: () => void;
}

export const SortableBlock = ({
  block,
  onUpdate,
  onDelete
}: SortableBlockProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id: block.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const BlockIcon = blockConfig[block.type]?.icon || Type;

  return (
    <>
      <div 
        ref={setNodeRef} 
        style={style} 
        className="group border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="p-3 flex items-center justify-between border-b py-[10px] px-[10px]">
            <div className="flex items-center gap-2">
              <button 
                {...attributes} 
                {...listeners} 
                className="cursor-grab hover:text-primary focus:outline-none" 
                aria-label="Drag to reorder"
              >
                <span className="w-5 h-5 text-gray-400 group-hover:text-gray-600 cursor-grab">⋮⋮</span>
              </button>
              <BlockIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">
                {blockConfig[block.type]?.label || 'Block'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDeleteDialog(true)} 
                className="text-gray-400 hover:text-red-600 p-1 h-auto"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-1 h-auto">
                  {isOpen ? 
                    <ChevronUp className="h-4 w-4 text-gray-500" /> : 
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  }
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
          <CollapsibleContent className="p-4">
            {block.type === "text" && <TextBlock block={block} onUpdate={onUpdate} />}
            {block.type === "image" && <ImageBlock block={block} onUpdate={onUpdate} />}
            {block.type === "button" && <ButtonBlock block={block} onUpdate={onUpdate} />}
            {block.type === "embed" && <EmbedBlock block={block} onUpdate={onUpdate} />}
            {block.type === "form" && <TextBlock block={block} onUpdate={onUpdate} />}
            {block.type === "posts" && <TextBlock block={block} onUpdate={onUpdate} />}
          </CollapsibleContent>
        </Collapsible>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this block.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
